import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Droplets, Zap, Plus, Calendar, Trash2, Pencil, Image as ImageIcon } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Alert, Button, Card, Badge, EmptyState, Skeleton, Modal, Input, FileUpload, ConfirmDialog } from '@/components/ui'
import { useMeter, useReadings, useCreateReading, useUpdateReading, useDeleteReading } from '@/hooks/useMeters'
import { useDateFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import { RolloverConfirmation } from '@/components/meters/RolloverConfirmation'
import { rolloverUsageFromError } from '@/lib/readingRollover'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { Reading } from '@/types'

interface ReadingFormData {
  previous_value: number
  current_value: number
  read_at: string
  image_urls: string[]
}

export function MeterReadingsPage() {
  const { t } = useTranslation('meters')
  const { t: tc } = useTranslation('common')
  const formatDate = useDateFormatter()
  const { timezone } = useLocalizationConfig()
  const today = localToday(timezone)
  const { id } = useParams<{ id: string }>()
  const meterId = Number(id)
  const navigate = useNavigate()

  const { data: meter, isLoading } = useMeter(meterId)
  const { data: readings, isLoading: isLoadingReadings, isFetching: isFetchingReadings } = useReadings(meterId)
  const translateError = useApiErrorTranslator('meters')
  const createReading = useCreateReading({ suppressErrorToast: true })
  const updateReading = useUpdateReading({ suppressErrorToast: true })
  const deleteReading = useDeleteReading()

  const [rolloverUsage, setRolloverUsage] = useState<number | null>(null)
  const [confirmedRollover, setConfirmedRollover] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const clearRollover = () => {
    setRolloverUsage(null)
    setConfirmedRollover(false)
    setSaveError(null)
  }
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingReading, setEditingReading] = useState<Reading | null>(null)
  const [showImageModal, setShowImageModal] = useState<string[] | null>(null)
  const [confirmDeleteReadingId, setConfirmDeleteReadingId] = useState<number | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    formState: { errors },
  } = useForm<ReadingFormData>({
    defaultValues: {
      previous_value: 0,
      current_value: 0,
      read_at: today,
      image_urls: [],
    },
  })

  const openAddModal = () => {
    const lastReading = sortedReadings?.[0]?.current_value ?? 0
    clearRollover()
    reset({
      previous_value: lastReading,
      current_value: 0,
      read_at: today,
      image_urls: [],
    })
    setEditingReading(null)
    setShowAddModal(true)
  }

  const openQuickAddModal = (reading: Reading) => {
    clearRollover()
    reset({
      previous_value: reading.current_value,
      current_value: 0,
      read_at: today,
      image_urls: [],
    })
    setEditingReading(null)
    setShowAddModal(true)
  }

  const openEditModal = (reading: Reading) => {
    clearRollover()
    reset({
      previous_value: reading.previous_value,
      current_value: reading.current_value,
      read_at: reading.read_at.split('T')[0],
      image_urls: reading.image_urls ?? [],
    })
    setEditingReading(reading)
    setShowAddModal(true)
  }

  const onAddReading = async (data: ReadingFormData) => {
    if (rolloverUsage !== null && !confirmedRollover) return
    setSaveError(null)
    const input = {
      meterId,
      ...data,
      ...(confirmedRollover ? { confirmed_rollover: true } : {}),
    }
    try {
      if (editingReading !== null) {
        await updateReading.mutateAsync({ id: editingReading.id, ...input })
      } else {
        await createReading.mutateAsync(input)
      }
      setShowAddModal(false)
      setEditingReading(null)
      clearRollover()
    } catch (error) {
      const usage = rolloverUsageFromError(error)
      if (usage !== null) {
        // A slow rejection must not ask the operator to confirm usage for
        // numbers they have already changed while the request was in flight.
        const current = getValues()
        if (current.previous_value !== data.previous_value || current.current_value !== data.current_value) return
        setRolloverUsage(usage)
        setConfirmedRollover(false)
      } else {
        setSaveError(translateError(error))
      }
    }
  }

  const handleDeleteReading = async () => {
    if (confirmDeleteReadingId === null) return
    await deleteReading.mutateAsync({ id: confirmDeleteReadingId, meterId })
    setConfirmDeleteReadingId(null)
  }

  const getMeterIcon = (type: string) => {
    return type === 'water' ? (
      <Droplets className="w-5 h-5 text-info" />
    ) : (
      <Zap className="w-5 h-5 text-warning" />
    )
  }

  const getMeterTypeLabel = (type: string) => {
    return type === 'water' ? t('types.water') : t('types.electricity')
  }

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card>
            <Skeleton className="h-48" />
          </Card>
        </div>
    )
  }

  if (!meter) {
    return (
        <Card>
          <EmptyState
            title={t('messages.not_found')}
            action={<Button onClick={() => navigate(-1)}>{tc('actions.back')}</Button>}
          />
        </Card>
    )
  }

  // Sort readings by date (newest first)
  const sortedReadings = readings?.slice().sort(
    (a, b) => new Date(b.read_at).getTime() - new Date(a.read_at).getTime()
  )
  const latestUsage = sortedReadings?.[0]?.usage ?? null

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: tc('nav.properties'), href: '/properties' },
            { label: t('meter_readings') },
          ]}
        />

        <PageHeader
          title={`${getMeterTypeLabel(meter.meter_type)} ${t('labels.meter')}`}
          description={meter.serial_number || t('empty.no_serial_number')}
          isRefreshing={isFetchingReadings && !isLoadingReadings}
          actions={
            <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={openAddModal}>
              {t('actions.add_reading')}
            </Button>
          }
        />

        {/* Meter Info */}
        <div className="grid gap-6 lg:grid-cols-3">
          <Card>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-base-200 flex items-center justify-center">
                {getMeterIcon(meter.meter_type)}
              </div>
              <div>
                <div className="text-sm text-base-content/60">{t('labels.meter_type')}</div>
                <div className="font-semibold">{getMeterTypeLabel(meter.meter_type)}</div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-base-200 flex items-center justify-center">
                <span className="text-lg font-bold text-primary">
                  {sortedReadings?.[0]?.current_value ?? 0}
                </span>
              </div>
              <div>
                <div className="text-sm text-base-content/60">{t('labels.latest_reading')}</div>
                <div className="font-semibold">
                  {sortedReadings?.[0]
                    ? formatDate(sortedReadings[0].read_at)
                    : t('empty.no_readings')}
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-base-200 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">
                  {latestUsage !== null ? latestUsage : '-'}
                </span>
              </div>
              <div>
                <div className="text-sm text-base-content/60">{t('labels.latest_usage')}</div>
                <div className="font-semibold">
                  {latestUsage !== null
                    ? `${latestUsage}`
                    : t('empty.need_two_readings')}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Reading History */}
        <Card>
          <h2 className="text-lg font-semibold mb-4">{t('labels.reading_history')}</h2>

          {isLoadingReadings ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : !sortedReadings || sortedReadings.length === 0 ? (
            <EmptyState
              icon={getMeterIcon(meter.meter_type)}
              title={t('empty.no_readings')}
              description={t('empty.no_readings_description')}
              action={
                <Button
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={openAddModal}
                >
                  {t('actions.add_reading')}
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{tc('labels.date')}</th>
                    <th>{t('labels.current_reading')}</th>
                    <th>{t('labels.usage')}</th>
                    <th>{t('labels.photo')}</th>
                    <th></th>
                    <th className="text-right">{tc('labels.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedReadings.map((reading) => {
                    return (
                      <tr key={reading.id} className="hover">
                        <td className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-base-content/60" />
                          {formatDate(reading.read_at)}
                        </td>
                        <td className="font-medium">{reading.current_value}</td>
                        <td>
                          <Badge variant={reading.usage > 0 ? 'primary' : 'ghost'}>
                            {reading.usage > 0 ? `+${reading.usage}` : reading.usage}
                          </Badge>
                        </td>
                        <td>
                          {reading.image_urls && reading.image_urls.length > 0 ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              leftIcon={<ImageIcon className="w-4 h-4" />}
                              onClick={() => setShowImageModal(reading.image_urls)}
                            >
                              {reading.image_urls.length}
                            </Button>
                          ) : (
                            <span className="text-base-content/40">-</span>
                          )}
                        </td>
                        <td>
                          <Button
                            size="xs"
                            variant="ghost"
                            leftIcon={<Plus className="w-4 h-4" />}
                            onClick={() => openQuickAddModal(reading)}
                            title={t('actions.add_reading')}
                            aria-label={t('actions.add_reading')}
                          />
                        </td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="xs"
                              variant="ghost"
                              leftIcon={<Pencil className="w-4 h-4" />}
                              onClick={() => openEditModal(reading)}
                              aria-label={tc('actions.edit')}
                            />
                            <Button
                              size="xs"
                              variant="ghost"
                              className="text-error"
                              leftIcon={<Trash2 className="w-4 h-4" />}
                              onClick={() => setConfirmDeleteReadingId(reading.id)}
                              aria-label={tc('actions.delete')}
                            />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <ConfirmDialog
        isOpen={confirmDeleteReadingId !== null}
        onClose={() => setConfirmDeleteReadingId(null)}
        onConfirm={handleDeleteReading}
        title={t('messages.delete_title')}
        message={t('messages.delete_confirm')}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteReading.isPending}
      />

      {/* Add / Edit Reading Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingReading(null) }}
        title={editingReading !== null ? t('actions.edit_reading') : t('actions.add_reading')}
        footer={
          <>
            <Button variant="ghost" onClick={() => { setShowAddModal(false); setEditingReading(null) }}>
              {tc('actions.cancel')}
            </Button>
            <Button
              onClick={handleSubmit(onAddReading)}
              disabled={rolloverUsage !== null && !confirmedRollover}
              loading={createReading.isPending || updateReading.isPending}
            >
              {t('actions.save_reading')}
            </Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={handleSubmit(onAddReading)}>
          {saveError && <Alert variant="error">{saveError}</Alert>}
          <Input
            label={t('labels.previous_reading')}
            type="number"
            placeholder={t('placeholders.previous_value')}
            error={errors.previous_value?.message}
            {...register('previous_value', {
              required: tc('errors.required'),
              valueAsNumber: true,
              onChange: clearRollover,
              min: { value: 0, message: tc('errors.positive_number') },
            })}
          />
          <Input
            label={t('labels.current_reading')}
            type="number"
            placeholder={t('placeholders.current_value')}
            error={errors.current_value?.message}
            {...register('current_value', {
              required: tc('errors.required'),
              valueAsNumber: true,
              onChange: clearRollover,
              min: { value: 0, message: tc('errors.positive_number') },
            })}
          />
          {rolloverUsage !== null && (
            <RolloverConfirmation usage={rolloverUsage} confirmed={confirmedRollover} onConfirm={setConfirmedRollover} />
          )}
          <Input
            label={t('labels.reading_date')}
            type="date"
            error={errors.read_at?.message}
            {...register('read_at', { required: tc('errors.required') })}
          />
          <Controller
            name="image_urls"
            control={control}
            render={({ field }) => (
              <FileUpload
                label={t('photo.evidence')}
                hint={t('photo.hint')}
                value={field.value}
                onChange={field.onChange}
                maxFiles={3}
              />
            )}
          />
        </form>
      </Modal>

      {/* Image Gallery Modal */}
      <Modal
        isOpen={!!showImageModal}
        onClose={() => setShowImageModal(null)}
        title={t('photo.title')}
      >
        {showImageModal && (
          <div className="grid grid-cols-1 gap-4">
            {showImageModal.map((url, index) => (
              <img
                key={index}
                src={url}
                alt={t('photo.reading_photo_alt', { count: index + 1 })}
                className="w-full rounded-lg"
              />
            ))}
          </div>
        )}
      </Modal>
    </>
  )
}
